import React, { useState } from "react";
import { Modal } from "../common/ui/modal";
import Button from "../common/ui/button/Button";
import { Copy, Check, ExternalLink } from "lucide-react";

interface CreateShareLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (params: CreateShareLinkParams) => Promise<{ publicUrl: string; id: string }>;
  resourceType?: "file" | "form_submission";
  onViewAllLinks?: () => void;
}

export interface CreateShareLinkParams {
  mode: "anyone" | "email_verified";
  recipientEmail?: string; // Required when mode is "email_verified"
  expiresInHours: number;
  maxDownloads: number | null;
}

export const CreateShareLinkModal: React.FC<CreateShareLinkModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  resourceType: _resourceType = "file",
  onViewAllLinks,
}) => {
  const [mode, setMode] = useState<"anyone" | "email_verified">("anyone");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [expirationHours, setExpirationHours] = useState(24);
  const [maxDownloads, setMaxDownloads] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate recipient email for email_verified mode
    if (mode === "email_verified" && !recipientEmail.trim()) {
      setError("Recipient email is required for email verification mode");
      setLoading(false);
      return;
    }

    try {
      const result = await onCreate({
        mode,
        recipientEmail: mode === "email_verified" ? recipientEmail.trim() : undefined,
        expiresInHours: expirationHours,
        maxDownloads,
      });
      setCreatedUrl(result.publicUrl);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to create share link";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUrl = async () => {
    if (createdUrl) {
      await navigator.clipboard.writeText(createdUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form state after a short delay to avoid flickering
    setTimeout(() => {
      setMode("anyone");
      setRecipientEmail("");
      setExpirationHours(24);
      setMaxDownloads(null);
      setCreatedUrl(null);
      setError(null);
      setCopied(false);
    }, 200);
  };

  const setOneTimeOnly = () => {
    setMaxDownloads(1);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={createdUrl ? "Share Link Created" : "Create Share Link"}>
      {!createdUrl ? (
        <form onSubmit={handleSubmit} className="space-y-6">
        {/* Access Mode */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Access Mode
          </label>
          <div className="space-y-3">
            <label className="flex items-start cursor-pointer">
              <input
                type="radio"
                value="anyone"
                checked={mode === "anyone"}
                onChange={(e) => setMode(e.target.value as "anyone")}
                className="mt-1 mr-3 h-4 w-4 text-brand-600 focus:ring-brand-500"
              />
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  Anyone with the link
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  No email verification required - easiest access
                </div>
              </div>
            </label>
            <label className="flex items-start cursor-pointer">
              <input
                type="radio"
                value="email_verified"
                checked={mode === "email_verified"}
                onChange={(e) => setMode(e.target.value as "email_verified")}
                className="mt-1 mr-3 h-4 w-4 text-brand-600 focus:ring-brand-500"
              />
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  Email verification required
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Recipient must verify email before accessing
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Recipient Email (only shown for email_verified mode) */}
        {mode === "email_verified" && (
          <div>
            <label
              htmlFor="recipientEmail"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Recipient Email <span className="text-red-500">*</span>
            </label>
            <input
              id="recipientEmail"
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="Enter recipient's email address"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              required
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Only this email address will be able to verify and access the shared content
            </p>
          </div>
        )}

        {/* Expiration */}
        <div>
          <label
            htmlFor="expiration"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Expiration <span className="text-red-500">*</span>
          </label>
          <select
            id="expiration"
            value={expirationHours}
            onChange={(e) => setExpirationHours(Number(e.target.value))}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            required
          >
            <option value={1}>1 hour</option>
            <option value={6}>6 hours</option>
            <option value={24}>24 hours</option>
            <option value={72}>3 days</option>
            <option value={168}>1 week</option>
            <option value={720}>30 days</option>
          </select>
        </div>

        {/* Max Downloads */}
        <div>
          <label
            htmlFor="maxDownloads"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Maximum Downloads
          </label>
          <div className="flex gap-2">
            <input
              id="maxDownloads"
              type="number"
              min="1"
              value={maxDownloads || ""}
              onChange={(e) =>
                setMaxDownloads(e.target.value ? Number(e.target.value) : null)
              }
              placeholder="Unlimited"
              className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={setOneTimeOnly}
            >
              One-time only
            </Button>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Leave empty for unlimited downloads, or set a specific limit
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-800 dark:text-red-200">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="md" disabled={loading}>
            {loading ? "Creating..." : "Create Share Link"}
          </Button>
        </div>
      </form>
      ) : (
        <div className="space-y-6">
          {/* Success Message */}
          <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4 border border-green-200 dark:border-green-800">
            <p className="text-sm text-green-800 dark:text-green-200">
              Your share link has been created successfully! Copy and share the link below.
            </p>
          </div>

          {/* Share URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Share Link
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={createdUrl}
                className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-mono text-sm"
              />
              <Button
                type="button"
                variant="outline"
                size="md"
                onClick={handleCopyUrl}
                startIcon={copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              >
                {copied ? "Copied!" : "Copy"}
              </Button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
            {onViewAllLinks && (
              <button
                type="button"
                onClick={() => {
                  handleClose();
                  onViewAllLinks();
                }}
                className="text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 font-medium flex items-center gap-1"
              >
                View all share links
                <ExternalLink className="h-3.5 w-3.5" />
              </button>
            )}
            <div className="flex gap-3 ml-auto">
              <Button
                type="button"
                variant="outline"
                size="md"
                onClick={handleClose}
              >
                Done
              </Button>
              <Button
                type="button"
                variant="primary"
                size="md"
                onClick={() => setCreatedUrl(null)}
              >
                Create Another
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};
